import React from "react";
import "./BsSNS.css";
import PageHeader from "../../components/headers/PageHeader";
import wave from "../../assets/Images/waves/wave_mou.png";

export default function BsSNS() {
    return (
        <div className="bssns-page">
            <PageHeader
                mediaSrc={wave}
                mediaType="image"
                subtitle="Nonclinical Biostatistics & SEND Support"
                title="Biostatistics Support for SEND and Nonclinical Studies"
            />

            <section className="bssns-section bssns-introSection">
                <h2>Scientific Statistical Support Beyond SEND Conversion</h2>

                <p>
                    Beyond SEND conversion, our scientific team provides specialized
                    statistical analyses supporting regulatory interpretation,
                    scientific defensibility, and submission readiness.
                </p>

                <p>
                    Eupraxia helps sponsors transform nonclinical study data into
                    submission-ready SEND packages along with tumor analyses and other
                    statistical analyses that support efficient regulatory review and
                    accelerate development timelines.
                </p>
            </section>

            <section className="bssns-section bssns-cardSection">
                <h2>General Toxicology Statistical Analyses</h2>

                <p className="bssns-sectionIntro">
                    We provide statistical support across core toxicology endpoints,
                    helping sponsors evaluate treatment-related effects, dose-response
                    patterns, and overall study interpretation.
                </p>

                <div className="bssns-analysisGrid">
                    <article className="bssns-analysisCard">Body weight analyses</article>
                    <article className="bssns-analysisCard">Food consumption analyses</article>
                    <article className="bssns-analysisCard">Clinical pathology evaluations</article>
                    <article className="bssns-analysisCard">Organ weight analyses</article>
                    <article className="bssns-analysisCard">Repeated measures analyses</article>
                    <article className="bssns-analysisCard">Trend analyses</article>
                    <article className="bssns-analysisCard">Dose-response evaluations</article>
                    <article className="bssns-analysisCard">Survival analyses</article>
                </div>
            </section>

            <section className="bssns-section bssns-tumorSection">
                <h2>Carcinogenicity and Tumor Analyses</h2>

                <p className="bssns-sectionIntro">
                    Our biostatisticians support comprehensive carcinogenicity
                    evaluations using industry-recognized methodologies for long-term
                    nonclinical studies and regulatory submissions.
                </p>

                <div className="bssns-cardGrid">
                    <article className="bssns-card">
                        <h3>Peto Analysis</h3>

                        <p>
                            We perform Peto-based analyses to support mortality-adjusted
                            tumor interpretation and long-term carcinogenicity assessment.
                        </p>

                        <ul className="bssns-list">
                            <li>Tumor incidence evaluation.</li>
                            <li>Mortality-adjusted tumor assessment.</li>
                            <li>Long-term carcinogenicity studies.</li>
                            <li>Regulatory submission support.</li>
                        </ul>
                    </article>

                    <article className="bssns-card">
                        <h3>Additional Tumor Evaluation Methods</h3>

                        <p>
                            We support additional statistical methods for tumor evaluation,
                            dose-response assessment, and contextual interpretation.
                        </p>

                        <ul className="bssns-list">
                            <li>Poly-k trend tests.</li>
                            <li>Cochran-Armitage trend tests.</li>
                            <li>Fisher&apos;s exact tests.</li>
                            <li>Survival-adjusted incidence analyses.</li>
                            <li>Historical control comparisons.</li>
                            <li>Dose-response modelling.</li>
                        </ul>
                    </article>
                </div>
            </section>

            <section className="bssns-section bssns-safetySection">
                <h2>Safety Pharmacology Statistical Support</h2>

                <p className="bssns-sectionIntro">
                    We provide statistical analyses for cardiovascular, respiratory,
                    and neurobehavioral safety pharmacology studies.
                </p>

                <div className="bssns-safetyGrid">
                    <article className="bssns-safetyCard">
                        <h3>Cardiovascular Studies</h3>

                        <ul className="bssns-list">
                            <li>ECG analyses.</li>
                            <li>Blood pressure analyses.</li>
                            <li>Heart rate analyses.</li>
                            <li>Telemetry studies.</li>
                        </ul>
                    </article>

                    <article className="bssns-safetyCard">
                        <h3>Respiratory Studies</h3>

                        <ul className="bssns-list">
                            <li>Respiratory rate analyses.</li>
                            <li>Tidal volume analyses.</li>
                            <li>Minute volume analyses.</li>
                        </ul>
                    </article>

                    <article className="bssns-safetyCard">
                        <h3>Neurobehavioral Studies</h3>

                        <ul className="bssns-list">
                            <li>Functional observational battery analyses.</li>
                            <li>Motor activity analyses.</li>
                            <li>Behavioral endpoints.</li>
                        </ul>
                    </article>
                </div>
            </section>

            <section className="bssns-section bssns-reportingSection">
                <h2>Regulatory Reporting Support</h2>

                <p className="bssns-sectionIntro">
                    Our biostatistics team assists with regulatory reporting,
                    scientific documentation, and health authority response support.
                </p>

                <div className="bssns-reportingGrid">
                    <article className="bssns-reportingCard">Statistical Analysis Plans</article>
                    <article className="bssns-reportingCard">Regulatory justifications</article>
                    <article className="bssns-reportingCard">Study report statistical sections</article>
                    <article className="bssns-reportingCard">SEND-to-report reconciliation</article>
                    <article className="bssns-reportingCard">Health authority response support</article>
                </div>
            </section>

            <section className="bssns-section bssns-whySection">
                <h2>Why Sponsors Choose Us</h2>

                <div className="bssns-whyGrid">
                    <article className="bssns-whyCard">
                        Deep expertise in toxicology and safety pharmacology
                    </article>

                    <article className="bssns-whyCard">
                        SEND specialists with scientific backgrounds
                    </article>

                    <article className="bssns-whyCard">
                        Integrated biostatistics and SEND capabilities
                    </article>

                    <article className="bssns-whyCard">
                        Regulatory-focused quality framework
                    </article>

                    <article className="bssns-whyCard">
                        Rapid turnaround capabilities
                    </article>

                    <article className="bssns-whyCard">
                        End-to-end submission readiness services
                    </article>

                    <article className="bssns-whyCard">
                        IND submission experience
                    </article>

                    <article className="bssns-whyCard">
                        FDA remediation expertise for Tox-Stats and SEND studies
                    </article>
                </div>
            </section>
        </div>
    );
}